import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useStravaSync } from "@/hooks/useStravaSync";
import { getSyncWarningMessage } from "@/i18n/syncWarnings";
import { localeAtom } from "@/state/locale.state";
import { syncModalOpenAtom, syncStateAtom } from "@/state/sync.state";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useAtom, useAtomValue } from "jotai";
import { Loader2, RefreshCcw } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { ConnectStravaButton } from "../strava/ConnectStravaButton";

function formatCount(n: number, locale: string) {
  return new Intl.NumberFormat(locale).format(n);
}

const AUTO_RETRY_MS = 3 * 60 * 1000;

export function SyncModal() {
  const [open, setOpen] = useAtom(syncModalOpenAtom);
  const sync = useAtomValue(syncStateAtom);

  const { startSync } = useStravaSync();

  // Auto-close behavior:
  // - close when a run finishes AND it succeeded (including "skipped fresh")
  // - do not auto-close on error (user should see it)
  const prevRunningRef = useRef(sync.isRunning);

  useEffect(() => {
    const wasRunning = prevRunningRef.current;
    const isRunning = sync.isRunning;
    prevRunningRef.current = isRunning;

    if (!open) return;

    // transition: running -> not running
    if (wasRunning && !isRunning) {
      const ok = sync.lastResult?.ok === true;
      if (ok) setOpen(false);
    }
  }, [open, setOpen, sync.isRunning, sync.lastResult?.ok]);

  const fetched = sync.progress.activities ?? 0;

  const locale = useAtomValue(localeAtom);
  const lastSyncedLabel = sync.lastSyncedAt
    ? new Date(sync.lastSyncedAt).toLocaleString(locale)
    : null;

  const warnings = sync.warnings ?? [];

  const offerRetry = useMemo(
    () => warnings.includes("sync.lock.busy"),
    [warnings],
  );

  const offerReLogin = useMemo(
    () => warnings.includes("sync.warning.missing_scope"),
    [warnings],
  );

  // Auto retry (only once per "lock busy" occurrence)
  const autoRetryTimeoutRef = useRef<number | null>(null);
  const autoRetriedRef = useRef(false);

  useEffect(() => {
    // Clear any pending timer when modal closes or warning disappears
    if (!open || !offerRetry) {
      if (autoRetryTimeoutRef.current != null) {
        window.clearTimeout(autoRetryTimeoutRef.current);
        autoRetryTimeoutRef.current = null;
      }
      autoRetriedRef.current = false;
      return;
    }

    // Only schedule when we're not currently running and haven't retried yet
    if (sync.isRunning) return;
    if (autoRetriedRef.current) return;

    // Avoid scheduling multiple timers
    if (autoRetryTimeoutRef.current != null) return;

    autoRetryTimeoutRef.current = window.setTimeout(() => {
      autoRetryTimeoutRef.current = null;
      autoRetriedRef.current = true;
      startSync();
    }, AUTO_RETRY_MS);

    return () => {
      if (autoRetryTimeoutRef.current != null) {
        window.clearTimeout(autoRetryTimeoutRef.current);
        autoRetryTimeoutRef.current = null;
      }
    };
  }, [open, offerRetry, sync.isRunning, startSync]);

  const handleManualResync = () => {
    if (autoRetryTimeoutRef.current != null) {
      window.clearTimeout(autoRetryTimeoutRef.current);
      autoRetryTimeoutRef.current = null;
    }
    autoRetriedRef.current = true;
    startSync();
  };

  const handleReLoginSuccess = () => {
    startSync();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
      }}
    >
      <DialogContent className="w-[100dvw] max-w-lg rounded-none p-0">
        <div className="flex h-full flex-col">
          <DialogHeader className="border-b p-4">
            <DialogTitle>
              <Trans>Syncing Strava</Trans>
            </DialogTitle>

            <DialogDescription className="sr-only">
              <Trans>
                Displays the progress and status of your Strava activity
                synchronization.
              </Trans>
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-1 items-center justify-center p-6">
            <div className="w-full max-w-md space-y-6">
              <div className="flex items-center justify-center">
                {sync.isRunning ? (
                  <Loader2 className="h-10 w-10 animate-spin" />
                ) : sync.error ? (
                  <div className="text-lg font-semibold">
                    <Trans>Sync stopped</Trans>
                  </div>
                ) : (
                  <div className="text-lg font-semibold">
                    <Trans>Sync complete</Trans>
                  </div>
                )}
              </div>

              <div className="space-y-2 text-center">
                <div className="text-sm text-muted-foreground">
                  <Trans>Status</Trans>
                </div>
                <div className="text-base font-medium">
                  {sync.status || (sync.isRunning ? t`Syncing…` : "—")}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3">
                <div className="rounded-lg border p-4">
                  <div className="text-sm text-muted-foreground">
                    <Trans>Activities fetched</Trans>
                  </div>
                  <div className="mt-1 text-2xl font-semibold tabular-nums">
                    {formatCount(fetched, locale)}
                  </div>
                </div>

                {sync.error ? (
                  <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4">
                    <div className="text-sm font-medium text-destructive">
                      <Trans>Error</Trans>
                    </div>
                    <div className="mt-1 text-sm">{sync.error}</div>
                  </div>
                ) : null}

                {warnings.length > 0 ? (
                  <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-medium">
                        <Trans>Warnings</Trans>
                      </div>

                      {offerRetry ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleManualResync}
                          disabled={sync.isRunning}
                        >
                          <RefreshCcw className="mr-2 h-4 w-4" />
                          <Trans>Refresh</Trans>
                        </Button>
                      ) : null}
                    </div>

                    <ul className="mt-2 space-y-1 text-sm">
                      {warnings.map((code) => (
                        <li key={code} className="flex gap-2">
                          <span className="mt-2 inline-block h-1.5 w-1.5 flex-none rounded-full bg-amber-500" />
                          <span>{getSyncWarningMessage(code)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {!sync.isRunning && !sync.error && lastSyncedLabel ? (
                  <div className="rounded-lg border p-4">
                    <div className="text-sm text-muted-foreground">
                      <Trans>Last sync</Trans>
                    </div>
                    <div className="mt-1 text-sm font-medium">
                      {lastSyncedLabel}
                    </div>
                  </div>
                ) : null}
              </div>

              {offerReLogin ? (
                <div className="flex justify-center">
                  <ConnectStravaButton
                    force={true}
                    onSuccess={handleReLoginSuccess}
                  />
                </div>
              ) : null}

              {sync.isRunning ? (
                <div className="text-center text-xs text-muted-foreground">
                  <Trans>
                    You can close this modal; sync continues in the background.
                  </Trans>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
